import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile'
import { ONBOARDING_STEP_NAMES } from '@/hooks/useOnboarding'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Circle, User, Image, Briefcase, Calendar, ChevronRight } from 'lucide-react'

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed'
const ONBOARDING_SKIPPED_STEPS_KEY = 'onboarding_skipped_steps'

interface TodoItem {
  id: string
  label: string
  done: boolean
  skipped: boolean
  link: string
  icon: typeof User
}

export function ProfileTodoList() {
  const { t } = useTranslation('dashboard')
  const { profile } = useProfile()

  // Only show after onboarding is completed
  const onboardingCompleted = localStorage.getItem(ONBOARDING_COMPLETED_KEY)
  if (onboardingCompleted !== 'true') {
    return null
  }

  // Get skipped steps from localStorage
  const getSkippedSteps = (): string[] => {
    try {
      const skipped = localStorage.getItem(ONBOARDING_SKIPPED_STEPS_KEY)
      return skipped ? JSON.parse(skipped) : []
    } catch {
      return []
    }
  }

  const skippedSteps = getSkippedSteps()

  const checklist: TodoItem[] = [
    {
      id: ONBOARDING_STEP_NAMES.STEP_1,
      label: t('profileTodo.items.businessName'),
      done: !!profile?.business_name,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_1),
      link: '/dashboard/profile/details',
      icon: User,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_2,
      label: t('profileTodo.items.profilePhoto'),
      done: !!profile?.profile_image,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_2),
      link: '/dashboard/profile/details',
      icon: User,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_3,
      label: t('profileTodo.items.bio'),
      done: !!profile?.bio,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_3),
      link: '/dashboard/profile/details',
      icon: User,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_4,
      label: t('profileTodo.items.galleryImages'),
      done: (profile?.gallery?.length || 0) >= 3,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_4),
      link: '/dashboard/profile/gallery',
      icon: Image,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_5,
      label: t('profileTodo.items.firstService'),
      done: (profile?.services?.length || 0) >= 1,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_5),
      link: '/dashboard/offerings/services',
      icon: Briefcase,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_6,
      label: t('profileTodo.items.availability'),
      done: (profile?.availability?.length || 0) > 0,
      skipped: skippedSteps.includes(ONBOARDING_STEP_NAMES.STEP_6),
      link: '/dashboard/hours/availability',
      icon: Calendar,
    },
  ]

  // Filter out completed and skipped items
  const incompleteItems = checklist.filter((item) => !item.done && !item.skipped)

  // Hide if all items are complete or all remaining items were skipped
  if (incompleteItems.length === 0) {
    return null
  }

  const completedCount = checklist.filter((item) => item.done).length
  const totalCount = checklist.filter((item) => !item.skipped).length
  const completionPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 100

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-foreground flex items-center justify-between">
          <span>{t('profileTodo.title')}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {t('profileTodo.completionPercent', { percent: completionPercent })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="h-2 bg-muted rounded-full">
            <div
              className="h-2 bg-primary rounded-full transition-all"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>

        {/* Progress text */}
        <p className="text-sm text-muted-foreground mb-4">
          {t('profileTodo.progressText', { completed: completedCount, total: totalCount })}
        </p>

        {/* Todo items list */}
        <ul className="space-y-2">
          {incompleteItems.map((item) => {
            const Icon = item.icon
            return (
              <li key={item.id}>
                <Link
                  to={item.link}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors group"
                >
                  <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm text-foreground flex-1">{item.label}</span>
                  <Icon className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Link>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
